import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import MarketplaceShell, {
  C,
  SERIF,
  card,
  ClosingBand,
  IconTile,
  pill,
} from "./MarketplaceShell";

/**
 * How it works, at /how-it-works — built from the Claude Design file
 * `How It Works.dc.html`.
 *
 * <p><b>Every step describes behaviour that exists.</b> The council filter, the nearest-first
 * fallback when nobody serves an area, the enquiry going to one business rather than being sold to
 * four, promoted labelling — each is something the code does. Where the design referred to another
 * page as plain text ("Trust &amp; safety", "see pricing") this makes it a real link, because those
 * pages now exist.</p>
 */

type Step = { title: string; body: ReactNode };

const CUSTOMER_STEPS: Step[] = [
  {
    title: "Tell us what you need, and where",
    body: "Search by the kind of work and the suburb or council your project sits in. No account to create, nothing to pay.",
  },
  {
    title: "See who actually serves that council",
    body: "Results are businesses who nominated your council area — not everyone on the platform. If nobody covers it yet, we say so and show the nearest instead.",
  },
  {
    title: "Check them over",
    body: "Every profile carries the business's ABN, how long they've been a member, the councils they work in, and photos of their own work.",
  },
  {
    title: "Contact them directly",
    body: "Send an enquiry or just call the number on the profile. Your enquiry goes to that business only — we don't sell the same job to four people.",
  },
];

const MEMBER_STEPS: Step[] = [
  {
    title: "Create your listing",
    body: "Add your logo, a cover photo of your own work, what you do, and the councils you serve. It stays a draft until you publish it.",
  },
  {
    title: "Turn up in the right searches",
    body: "You appear for the councils you nominated and the services you listed — so every enquiry is work you can actually take.",
  },
  {
    title: "Reply, and keep track",
    body: "Enquiries arrive in your inbox on the site. Mark each one handled, won or lost — that's what builds your response figure.",
  },
  {
    title: "See whether it's working",
    body: "Your marketplace page shows profile views, distinct visitors, and how often you appeared in search. Real numbers, not a score.",
  },
];

const FAQS: { icon: string; title: string; body: ReactNode }[] = [
  {
    icon: "🗺",
    title: "Where the projects come from",
    body: "Councils publish development applications as they're lodged — new builds, extensions, fit-outs, demolitions. We collect them across South Australia, southern Queensland and New South Wales. Members on the Intelligence plan can watch councils and suburbs, and get alerted while a project is still being planned.",
  },
  {
    icon: "$",
    title: "What it costs you as a customer",
    body: "Nothing. We don't charge to search, view a profile or send an enquiry, and we take no commission. Members pay a flat monthly subscription to be listed — that's the whole of it.",
  },
  {
    icon: "★",
    title: "About featured listings",
    body: "A business can pay to sit at the top of the marketplace for a week, labelled as promoted wherever it appears. Featured means they bought the spot — not that we rate them above the others.",
  },
  {
    icon: "⚖",
    title: "What we don't do",
    // The design names Trust & safety as plain text. It is a page now, so it is a link.
    body: (
      <>
        We're a directory, not a vetting service — we don't inspect work, check licences or insurance,
        or supervise a job, and we're not a party to your agreement with a business.{" "}
        <Link to="/trust">Trust &amp; safety</Link> sets out what we do and don't check.
      </>
    ),
  },
];

/**
 * The numbered step list. The connector runs between circles and stops after the last one, which is
 * what `line: i < length - 1` does in the design's own data.
 */
function Steps({ steps, dark }: { steps: Step[]; dark: boolean }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
      {steps.map((step, i) => (
        <div key={step.title} style={{ display: "flex", gap: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: "none" }}>
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 99,
                background: dark ? "rgba(134,239,172,.18)" : C.tint,
                border: `1px solid ${dark ? "rgba(134,239,172,.5)" : "#cfe0d4"}`,
                color: dark ? C.mint : C.deep,
                display: "grid",
                placeItems: "center",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {i + 1}
            </span>
            {i < steps.length - 1 && (
              <span
                style={{
                  width: 1,
                  flex: 1,
                  background: dark ? "rgba(134,239,172,.25)" : C.bandLine,
                  margin: "4px 0",
                }}
              />
            )}
          </div>
          <div style={{ paddingBottom: 22, minWidth: 0 }}>
            <div style={{ fontSize: 15.5, fontWeight: 700, marginBottom: 5, color: dark ? "#fff" : C.ink }}>
              {step.title}
            </div>
            <div style={{ fontSize: 13.5, color: dark ? C.mintInk : C.bodyMuted, lineHeight: 1.6 }}>
              {step.body}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function PartnerNetworkHowItWorks() {
  return (
    <MarketplaceShell
      metaTitle="How it works"
      metaDescription="How to find a trade for your project on DAconnect, and how a listing works if you're the one being found."
      eyebrow="HOW IT WORKS"
      title={
        <>
          {/* Mint, not deep green: the accent sits on a deep-green wash over the photograph, and
              deep-on-deep is the one pairing in this palette that disappears. */}
          One marketplace, <b style={{ color: C.mint, fontWeight: 600 }}>two sides</b>.
        </>
      }
      intro="People with a project who need someone to do the work — and the trades and suppliers who do it. Here's what happens on each."
      heroImage={{ src: "/images/partner-network/how-it-works-hero.jpg" }}
      showBack
    >
      <div
        style={{
          display: "grid",
          // auto-fit handles the narrow viewport on its own; no media query needed.
          gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))",
          gap: 24,
          alignItems: "stretch",
        }}
      >
        {/* CUSTOMERS */}
        <section
          data-testid="how-customers"
          style={{
            background: C.deep,
            color: "#fff",
            borderRadius: 20,
            padding: "34px 34px 30px",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 20px 50px rgba(20,83,45,.18)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, gap: 12 }}>
            <h2 style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 500, margin: 0 }}>
              If you're building something
            </h2>
            <span
              style={{
                background: C.mint,
                color: C.deep,
                fontSize: 11,
                fontWeight: 700,
                borderRadius: 99,
                padding: "4px 12px",
                flex: "none",
              }}
            >
              FREE
            </span>
          </div>
          <div style={{ fontSize: 13.5, color: C.mintFaint, marginBottom: 26 }}>No account needed.</div>
          <Steps steps={CUSTOMER_STEPS} dark />
          <Link
            to="/find"
            className="dcx-pill"
            style={{ ...pill, alignSelf: "flex-start", background: C.mint, color: C.deep, marginTop: 6 }}
          >
            Search the marketplace
          </Link>
        </section>

        {/* TRADES */}
        <section
          data-testid="how-trades"
          style={{
            ...card,
            padding: "34px 34px 30px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, gap: 12 }}>
            <h2 style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 500, margin: 0, color: C.ink }}>
              If you're a trade or supplier
            </h2>
            <span
              style={{
                background: C.tint,
                color: C.deep,
                fontSize: 11,
                fontWeight: 700,
                borderRadius: 99,
                padding: "4px 12px",
                flex: "none",
              }}
            >
              SUBSCRIPTION
            </span>
          </div>
          <div style={{ fontSize: 13.5, color: C.bodyMuted, marginBottom: 26 }}>
            {/* The design pointed this at #pricing; the page exists, so it goes there. */}
            A flat monthly fee — <Link to="/pricing" style={{ fontWeight: 700 }}>see pricing</Link>. No
            pay-per-lead, no commission.
          </div>
          <Steps steps={MEMBER_STEPS} dark={false} />
          <Link
            to="/signup"
            className="dcx-pill"
            style={{ ...pill, alignSelf: "flex-start", background: C.deep, color: "#fff", marginTop: 6 }}
          >
            List your business
          </Link>
        </section>
      </div>

      {/* FINE PRINT */}
      <section
        data-testid="how-fine-print"
        style={{
          ...card,
          marginTop: 24,
          padding: 34,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
          gap: "28px 36px",
        }}
      >
        <div style={{ gridColumn: "1/-1", display: "flex", alignItems: "center", gap: 12 }}>
          <h2 style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 500, color: C.ink, margin: 0 }}>
            The fine print, plainly
          </h2>
          <span style={{ flex: 1, height: 1, background: C.line }} />
        </div>
        {FAQS.map((faq) => (
          <div key={faq.title} style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <IconTile glyph={faq.icon} size={34} />
              <h3 style={{ fontSize: 15, fontWeight: 700, color: C.ink, margin: 0 }}>{faq.title}</h3>
            </div>
            <div style={{ fontSize: 13.5, color: C.bodyMuted, lineHeight: 1.65 }}>{faq.body}</div>
          </div>
        ))}
      </section>

      <ClosingBand
        heading="Still got a question?"
        detail="We answer every message — usually the same day."
        actionLabel="Get in touch"
        actionTo="/contact"
      />
    </MarketplaceShell>
  );
}
